<template>
  <v-container>
    <v-row align="center" class="pt-4 mb-2">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="searchTerm"
          label="Filtrar por email..."
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="auto"
        class="d-flex justify-sm-end"
        v-if="canCreate"
      >
        <v-btn
          color="primary"
          :variant="showNewGrantForm ? 'tonal' : 'flat'"
          @click="showNewGrantForm = !showNewGrantForm"
        >
          <v-icon start :icon="showNewGrantForm ? mdiClose : mdiPlus" />
          {{ showNewGrantForm ? "Cancelar" : "Novo Acesso" }}
        </v-btn>
      </v-col>
    </v-row>

    <v-expand-transition>
      <v-card
        v-if="showNewGrantForm"
        class="mb-4 pa-4"
        style="max-width: 660px"
      >
        <tenant-grant-form
          :tenant-id="tenantId"
          @close="showNewGrantForm = false"
          @saved="onGrantCreated"
        />
      </v-card>
    </v-expand-transition>

    <v-data-table-server
      v-model:page="page"
      v-model:items-per-page="perPage"
      :loading="isLoading"
      :headers="headers"
      :items="filteredUsers"
      :items-length="totalItems"
      :items-per-page-options="[10, 25, 50, 100]"
      hover
      @click:row="onEditUser"
    >
      <template #loading>
        <v-skeleton-loader type="table-row@5" />
      </template>

      <template #no-data>
        <div class="text-center py-8">
          <v-icon
            size="48"
            color="medium-emphasis"
            :icon="mdiAccountGroup"
            class="mb-3"
          />
          <div class="text-body-1 text-medium-emphasis">
            Nenhum acesso encontrado
          </div>
          <div class="text-body-2 text-disabled mt-1">
            {{
              searchTerm
                ? "Tente alterar o filtro de busca"
                : "Adicione um novo acesso para começar"
            }}
          </div>
        </div>
      </template>

      <template #[`item.role`]="{ item }">
        <v-chip size="small" variant="tonal" color="primary">
          {{ item.grants?.[0]?.role?.description || "N/A" }}
        </v-chip>
      </template>

      <template #[`item.is_active`]="{ item }">
        <v-chip
          v-if="item.grants?.[0]?.is_active"
          size="small"
          variant="tonal"
          color="success"
          :prepend-icon="mdiCheckCircleOutline"
        >
          Ativo
        </v-chip>
        <v-chip
          v-else
          size="small"
          variant="tonal"
          color="error"
          :prepend-icon="mdiAccountOff"
        >
          Desativado
        </v-chip>
      </template>

      <template #[`item.is_email_confirmed`]="{ item }">
        <v-chip
          v-if="item.is_email_confirmed"
          size="small"
          variant="tonal"
          color="success"
          :prepend-icon="mdiCheckCircleOutline"
        >
          Confirmado
        </v-chip>
        <span v-else class="d-inline-flex align-center ga-2">
          <v-chip
            size="small"
            variant="tonal"
            color="warning"
            :prepend-icon="mdiAlertCircleOutline"
          >
            Pendente
          </v-chip>
          <v-btn
            size="x-small"
            color="primary"
            variant="text"
            :loading="resendingConfirmation === item.id"
            :disabled="cooldownUserIds.has(item.id)"
            @click.stop="resendConfirmation(item)"
          >
            {{ cooldownUserIds.has(item.id) ? "Enviado" : "Reenviar" }}
            <v-icon end size="14" :icon="mdiEmail" />
          </v-btn>
        </span>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  mdiPlus,
  mdiClose,
  mdiEmail,
  mdiAccountGroup,
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiAccountOff,
} from "@mdi/js";
import {
  useUsersQuery,
  useResendConfirmationMutation,
} from "@/queries/modules/users";
import TenantGrantForm from "@/views/company/TenantGrantForm.vue";
import { useSnackbar } from "@/plugins/snackbar";

const route = useRoute();
const router = useRouter();
const $snackbar = useSnackbar();
const $can = inject("$can");

const searchTerm = ref("");
const showNewGrantForm = ref(false);
const page = ref(1);
const perPage = ref(25);
const resendingConfirmation = ref(null);
const cooldownUserIds = ref(new Set());

const tenantId = computed(() =>
  route.params.tenant_id ? Number(route.params.tenant_id) : null,
);

const headers = [
  { title: "E-mail", key: "email" },
  { title: "Perfil", key: "role" },
  { title: "Status", key: "is_active" },
  { title: "Status do email", key: "is_email_confirmed" },
];

const queryParams = computed(() => ({
  tenant_id: tenantId.value,
  role_type: "primary",
  limit: perPage.value,
  offset: (page.value - 1) * perPage.value,
}));

const { data, isLoading, refetch } = useUsersQuery(queryParams);

const users = computed(() => data.value?.items || []);
const totalItems = computed(() => data.value?.total || 0);

const filteredUsers = computed(() => {
  return users.value.filter((usr) => {
    return usr.email.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

const canCreate = computed(() => $can && $can("grant:create"));

const resendMutation = useResendConfirmationMutation();

const onGrantCreated = () => {
  showNewGrantForm.value = false;
  refetch();
};

const onEditUser = (_, { item }) => {
  router.push({
    name: "tenant.grants.edit",
    params: {
      tenant_id: tenantId.value,
      user_id: item.id,
    },
  });
};

const resendConfirmation = async (user) => {
  if (cooldownUserIds.value.has(user.id)) return;
  resendingConfirmation.value = user.id;
  try {
    await resendMutation.mutateAsync(user.id);
    $snackbar.success(
      "O link de confirmação foi enviado para o email do usuário.",
    );
    cooldownUserIds.value.add(user.id);
    setTimeout(() => cooldownUserIds.value.delete(user.id), 30_000);
  } catch (e) {
    $snackbar.error(
      e.message ||
        "Ocorreu um erro ao enviar o link de confirmação, tente novamente.",
    );
  } finally {
    resendingConfirmation.value = null;
  }
};
</script>

<style scoped>
:deep(.v-data-table tbody tr) {
  cursor: pointer;
}
</style>
